import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Divider, Grid, IconButton,
  InputAdornment, MenuItem, Paper, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import WifiIcon from "@mui/icons-material/Wifi";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StorageIcon from "@mui/icons-material/Storage";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import StatCard from "../components/StatCard";
import DeviceDrawer from "../components/DeviceDrawer";
import { DevicesAPI } from "../api/devices";
import { AssetsAPI } from "../api/assets";
import { EnterprisesAPI } from "../api/enterprises";
import { useAuth } from "../providers/AuthProvider";

function getDeviceStatus(device) {
  const contact = device?.last_heartbeat_at || device?.last_seen;
  if (!contact) return { label: "Unknown", color: "default" };
  const hours = (Date.now() - new Date(contact).getTime()) / 3600000;
  if (hours <= .5) return { label: "Online", color: "success" };
  if (hours <= 4) return { label: "Delayed", color: "warning" };
  if (hours <= 24) return { label: "Offline", color: "error" };
  return { label: "Stale", color: "default" };
}

function getStorage(device) {
  const entries = Array.isArray(device?.hw_meta?.filesystems) ? device.hw_meta.filesystems : [];
  const volumes = entries.filter((volume) => Number(volume?.size) > 0);
  const capacity = volumes.reduce((total, volume) => total + Number(volume.size || 0), 0);
  const used = volumes.reduce((total, volume) => total + Math.min(Number(volume.used || 0), Number(volume.size || 0)), 0);
  const percentage = capacity ? Math.round((used / capacity) * 100) : null;
  return { capacity, used, percentage };
}

function formatGb(bytes) {
  const value = Number(bytes);
  return Number.isFinite(value) && value > 0 ? `${(value / 1024 ** 3).toFixed(1)} GB` : "—";
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : "Never";
}

function storageLabel(device) {
  const storage = getStorage(device);
  return storage.percentage == null ? "No data" : `${storage.percentage}% · ${formatGb(storage.used)} / ${formatGb(storage.capacity)}`;
}

function assignedAssetForDevice(device, assetsByDevice) {
  return assetsByDevice[device?.id] || null;
}

function assignedLabel(device, assetsByDevice) {
  const asset = assignedAssetForDevice(device, assetsByDevice);
  return asset?.asset_custodian || device?.owner_name || device?.assigned_to || "";
}

function exportDevicesCSV(devices, assetCounts, assetsByDevice, enterpriseName) {
  const headings = ["Device", "Status", "Assigned to", "Operating system", "Storage", "Asset match", "Last heartbeat"];
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const rows = devices.map((device) => [
    device.hostname || device.device_uuid || "",
    getDeviceStatus(device).label,
    assignedLabel(device, assetsByDevice),
    device.os_info?.distro || device.os_name || device.os || "",
    storageLabel(device),
    assetCounts[device.id] ? "Matched" : "Not matched",
    device.last_heartbeat_at || device.last_seen || "",
  ]);
  const csv = [headings.map(escape).join(","), ...rows.map((row) => row.map(escape).join(","))].join("\n");
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${String(enterpriseName || "enterprise").toLowerCase().replace(/[^a-z0-9]+/g, "_")}_devices_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function MonitorAssets() {
  const auth = useAuth();
  const [devices, setDevices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [enterprise, setEnterprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [deviceSort, setDeviceSort] = useState({ by: "last_heartbeat", dir: "desc" });
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      setLoading(true);
      const [deviceResult, assetResult] = await Promise.allSettled([DevicesAPI.list(), AssetsAPI.list()]);
      if (!mounted) return;
      const devicePayload = deviceResult.status === "fulfilled" ? deviceResult.value : null;
      const assetPayload = assetResult.status === "fulfilled" ? assetResult.value : null;
      setDevices(devicePayload?.data && Array.isArray(devicePayload.data) ? devicePayload.data : (devicePayload?.data?.data || []));
      setAssets(assetPayload?.data?.data || assetPayload?.data || []);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [refreshKey]);

  useEffect(() => {
    if (auth?.user?.role !== "enterprise_admin") return;
    void EnterprisesAPI.getMyEnterprise()
      .then((response) => setEnterprise(response?.data?.data || null))
      .catch(() => setEnterprise(null));
  }, [auth?.user?.role]);

  const assetCounts = useMemo(() => {
    const counts = {};
    assets.forEach((asset) => {
      if (["RETIRED", "DISPOSED", "SCRAP"].includes(String(asset.asset_status || "").toUpperCase())) return;
      if (asset.device_id) counts[asset.device_id] = (counts[asset.device_id] || 0) + 1;
    });
    return counts;
  }, [assets]);

  const assetsByDevice = useMemo(() => {
    const mapped = {};
    assets.forEach((asset) => {
      if (!asset.device_id || ["RETIRED", "DISPOSED", "SCRAP"].includes(String(asset.asset_status || "").toUpperCase())) return;
      if (!mapped[asset.device_id]) mapped[asset.device_id] = asset;
    });
    return mapped;
  }, [assets]);

  const stats = useMemo(() => {
    let online = 0; let delayed = 0; let storageRisk = 0; let unmatched = 0;
    devices.forEach((device) => {
      const status = getDeviceStatus(device);
      const storage = getStorage(device);
      if (status.label === "Online") online += 1;
      if (status.label === "Delayed" || status.label === "Offline" || status.label === "Stale") delayed += 1;
      if (storage.percentage != null && storage.percentage >= 80) storageRisk += 1;
      if (!assetCounts[device.id]) unmatched += 1;
    });
    return { online, delayed, storageRisk, unmatched };
  }, [devices, assetCounts]);

  const filteredDevices = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = devices.filter((device) => {
      const matchesStatus = statusFilter === "ALL" || getDeviceStatus(device).label.toUpperCase() === statusFilter;
      const asset = assignedAssetForDevice(device, assetsByDevice);
      const searchable = [device.hostname, device.device_uuid, device.owner_name, device.assigned_to, asset?.asset_uid, asset?.asset_custodian, asset?.owner_name, device.os_info?.distro, device.os_name, device.os].join(" ").toLowerCase();
      return matchesStatus && (!term || searchable.includes(term));
    });
    const valueFor = (device) => {
      const storage = getStorage(device);
      if (deviceSort.by === "status") return getDeviceStatus(device).label;
      if (deviceSort.by === "storage") return storage.percentage == null ? -1 : storage.percentage;
      if (deviceSort.by === "asset_record") return assetCounts[device.id] ? 1 : 0;
      if (deviceSort.by === "last_heartbeat") return new Date(device.last_heartbeat_at || device.last_seen || 0).getTime();
      if (deviceSort.by === "assigned_to") return assignedLabel(device, assetsByDevice);
      return device.hostname || device.device_uuid || "";
    };
    const direction = deviceSort.dir === "asc" ? 1 : -1;
    return rows.sort((left, right) => {
      const a = valueFor(left);
      const b = valueFor(right);
      if (typeof a === "number" && typeof b === "number") return (a - b) * direction;
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }) * direction;
    });
  }, [assetCounts, assetsByDevice, deviceSort, devices, search, statusFilter]);

  const requestDeviceSort = (by) => {
    setDeviceSort((current) => ({ by, dir: current.by === by && current.dir === "asc" ? "desc" : "asc" }));
  };

  return <Box sx={{ width: "100%", py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" mb={3} gap={2}>
      <Box><Typography variant="h5" fontWeight={800}>Monitor Devices</Typography><Typography variant="body2" color="text.secondary">Review device check-ins, capacity and asset reconciliation.</Typography></Box>
      <Tooltip title="Refresh device data"><IconButton onClick={() => setRefreshKey((value) => value + 1)}><RefreshIcon /></IconButton></Tooltip>
    </Box>

    <Grid container spacing={{ xs: 1.1, sm: 1.5, md: 2 }} sx={{ mb: { xs: 1.8, sm: 2.4, md: 3 } }}>
      <Grid item xs={4} sm={4} md={2.4}><StatCard label="Total Devices" value={devices.length} icon={<DevicesOtherIcon />} /></Grid>
      <Grid item xs={4} sm={4} md={2.4}><StatCard label="Online" value={stats.online} icon={<WifiIcon />} /></Grid>
      <Grid item xs={4} sm={4} md={2.4}><StatCard label="Need Check-in" value={stats.delayed} icon={<AccessTimeIcon />} /></Grid>
      <Grid item xs={6} sm={4} md={2.4}><StatCard label="Storage Risk" value={stats.storageRisk} icon={<StorageIcon />} /></Grid>
      <Grid item xs={6} sm={4} md={2.4}><StatCard label="Not Matched" value={stats.unmatched} icon={<LinkOffIcon />} /></Grid>
    </Grid>

    <Paper sx={(theme) => ({
      p: { xs: 1.1, sm: 1.6, md: 2 },
      borderRadius: 2,
      height: { xs: "calc(100vh - 260px)", sm: "calc(100vh - 285px)" },
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
    })}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.1, sm: 1.6 }} mb={{ xs: 1.1, sm: 1.8 }} alignItems={{ xs: "stretch", sm: "center" }}>
        <TextField size="small" placeholder="Search device / owner / OS" value={search} onChange={(event) => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ minWidth: { xs: "100%", sm: 250 } }} />
        <Select size="small" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} sx={{ minWidth: { xs: "100%", sm: 165 } }}>
          <MenuItem value="ALL">All status</MenuItem><MenuItem value="ONLINE">Online</MenuItem><MenuItem value="DELAYED">Delayed</MenuItem><MenuItem value="OFFLINE">Offline</MenuItem><MenuItem value="STALE">Stale</MenuItem><MenuItem value="UNKNOWN">Unknown</MenuItem>
        </Select>
        <Box flex={1} />
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportDevicesCSV(filteredDevices, assetCounts, assetsByDevice, enterprise?.name)}>Export</Button>
      </Stack>
      <Divider sx={{ mb: 1 }} />
      {loading ? <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}><CircularProgress /></Box> : <TableContainer sx={{ flex: 1 }}><Table stickyHeader size="small"><TableHead><TableRow>
        {[
          ["device", "Device", 220], ["status", "Status", 135], ["assigned_to", "Assigned to", 180],
          ["storage", "Storage", 210], ["asset_record", "Asset record", 130], ["last_heartbeat", "Last heartbeat"],
        ].map(([by, label, width]) => <TableCell key={by} width={width} sortDirection={deviceSort.by === by ? deviceSort.dir : false}><TableSortLabel active={deviceSort.by === by} direction={deviceSort.by === by ? deviceSort.dir : "asc"} onClick={() => requestDeviceSort(by)}>{label}</TableSortLabel></TableCell>)}
      </TableRow></TableHead><TableBody>
        {filteredDevices.length ? filteredDevices.map((device) => { const status = getDeviceStatus(device); const storage = getStorage(device); const asset = assignedAssetForDevice(device, assetsByDevice); const hasAsset = Boolean(assetCounts[device.id]); return <TableRow key={device.id || device.device_uuid} hover onClick={() => setSelectedDevice(device)} sx={{ cursor: "pointer", "&:hover": { bgcolor: "#F8FAFC" } }}>
          <TableCell><Stack spacing={.15}><Typography variant="body2" fontWeight={600}>{device.hostname || device.device_uuid || "Managed device"}</Typography><Typography variant="caption" color="text.secondary">{device.os_info?.distro || device.os_name || device.os || "OS not reported"}</Typography></Stack></TableCell>
          <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
          <TableCell><Stack spacing={0.15}><Typography variant="body2" fontWeight={600}>{assignedLabel(device, assetsByDevice) || "—"}</Typography><Typography variant="caption" color="text.secondary">{asset ? `${asset.asset_uid} · Owner: ${asset.owner_name || "Not set"}` : "No asset linked"}</Typography></Stack></TableCell>
          <TableCell>{storage.percentage == null ? <Typography variant="body2" color="text.secondary">No data</Typography> : <Stack spacing={.2}><Typography variant="body2" fontWeight={600}>{storage.percentage}% used</Typography><Typography variant="caption" color="text.secondary">{formatGb(storage.used)} of {formatGb(storage.capacity)}</Typography></Stack>}</TableCell>
          <TableCell><Chip size="small" label={hasAsset ? "Matched" : "Not matched"} variant="outlined" color={hasAsset ? "success" : "default"} /></TableCell>
          <TableCell>{formatDate(device.last_heartbeat_at || device.last_seen)}</TableCell>
        </TableRow>; }) : <TableRow><TableCell colSpan={6} align="center">No devices match this view.</TableCell></TableRow>}
      </TableBody></Table></TableContainer>}
    </Paper>
    <DeviceDrawer open={Boolean(selectedDevice)} device={selectedDevice} onClose={() => setSelectedDevice(null)} />
  </Box>;
}
